/* Zerafite scroll story (sections/zf-scroll-story.liquid). Vanilla, no libraries, loaded with defer.
   The layout is chosen in CSS. This script only runs the pinned layout: it marks the active scene,
   and, on capable devices, loads frame sequences after the visitor's first scroll or touch and
   draws them on one canvas. Frames are loaded with Image objects, never with fetch. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('zf-scroll-story')) return;

  var HOLD = 0.85; // a sequence ends at 85% of its scene, then holds its last frame
  var FIRST = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'];
  var QUERIES = ['(max-width: 749.98px)', '(prefers-reduced-motion: reduce)', '(max-height: 479.98px)'];

  function clamp(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  class ZfScrollStory extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.ready = false;
      this.teardown();
      this.mqs.forEach((mq) => mq.removeEventListener && mq.removeEventListener('change', this.setup));
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.remeasure);
      this.removeEventListener('shopify:block:select', this.onSelect);
    }

    init() {
      if (this.ready || !this.isConnected) return;
      // The rating snippet puts aria-label on a plain div; give it a role so the label is valid.
      this.querySelectorAll('.zfss__rating [aria-label]:not([role])').forEach((el) => el.setAttribute('role', 'img'));
      this.pin = this.querySelector('.zfss__pin');
      this.stage = this.querySelector('.zfss__stage');
      this.canvas = this.querySelector('.zfss__canvas');
      this.rail = this.querySelector('.zfss__rail-fill');
      if (!this.pin || !this.stage || !this.canvas || !window.matchMedia) return;
      this.ready = true;
      ['setup', 'tick', 'schedule', 'remeasure', 'onFirst', 'onSelect'].forEach((m) => (this[m] = this[m].bind(this)));
      this.mqs = QUERIES.map((q) => window.matchMedia(q));
      this.mqs.forEach((mq) => mq.addEventListener && mq.addEventListener('change', this.setup));
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.remeasure, { passive: true });
      this.addEventListener('shopify:block:select', this.onSelect);
      this.setup();
    }

    /* Runs at start and whenever the breakpoint, the motion setting or the viewport height class changes. */
    setup() {
      this.teardown();
      var pos = getComputedStyle(this.stage).position;
      if (pos !== 'sticky' && pos !== '-webkit-sticky') return; // stacked stills: CSS has done everything

      var mobile = this.mqs[0].matches;
      var c = navigator.connection || {};
      var ua = navigator.userAgent;
      var ios = /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
      // Lite tier: same pinned layout, posters only, no frames requested.
      this.lite =
        this.dataset.sequences === 'false' ||
        (mobile && this.dataset.mobileSequences === 'false') ||
        !!c.saveData ||
        /2g/.test(c.effectiveType || '') ||
        navigator.deviceMemory <= 2 ||
        typeof createImageBitmap !== 'function';
      this.stride = mobile ? 2 : 1; // phones load every second frame
      this.maxDecoded = ios ? 16 : mobile ? 20 : 32; // cap on decoded frames held in memory
      this.parallel = mobile ? 4 : 6;
      this.size = mobile ? 640 : 960;
      if (!this.lite) {
        this.canvas.width = this.canvas.height = this.size;
        this.ctx = this.canvas.getContext('2d');
        if (!this.ctx) this.lite = true;
      }

      var layers = this.querySelectorAll('.zfss__layer');
      var base = 0;
      this.scenes = Array.prototype.map.call(this.querySelectorAll('.zfss__step'), (el, id) => {
        var url = (mobile ? el.dataset.framesM : el.dataset.framesD) || '';
        var n = this.lite || url.lastIndexOf('-001.') < 0 ? 0 : parseInt(el.dataset.count, 10) || 0;
        var s = { id: id, el: el, card: el.querySelector('.zfss__card'), layer: layers[id] || null, n: n > 1 ? n : 0, url: url, bg: el.dataset.bg || '', base: base, imgs: [], top: 0, h: 1, pad: 0, exit: 1 };
        base += s.n;
        return s;
      });
      if (!this.scenes.length) return;

      this.live = true;
      this.cache = new Map(); // global frame number -> ImageBitmap
      this.pending = new Set();
      this.queue = [];
      this.inflight = [];
      this.active = -1;
      this.target = 0;
      this.drawn = -1;
      this.drawnScene = -9;
      this.fails = 0;
      this.loads = 0;
      this.interacted = window.scrollY > 0 || !!(window.Shopify && window.Shopify.designMode);
      this.classList.add('zfss--live');
      this.measure();
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.remeasure);
        this.ro.observe(this.pin);
      }
      // Work only while the story is within one screen of the viewport.
      this.io = new IntersectionObserver(
        (entries) => {
          this.near = entries[entries.length - 1].isIntersecting;
          if (this.near) {
            this.load();
            this.schedule();
          } else {
            this.flush();
          }
        },
        { rootMargin: '100% 0px' }
      );
      this.io.observe(this.pin);
      // Network gate: no frame is requested before the visitor does something.
      if (!this.interacted) FIRST.forEach((t) => window.addEventListener(t, this.onFirst, { passive: true }));
      this.schedule();
    }

    teardown() {
      if (!this.live) return;
      this.live = false;
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      if (this.ro) this.ro.disconnect();
      if (this.io) this.io.disconnect();
      FIRST.forEach((t) => window.removeEventListener(t, this.onFirst));
      this.loadGen = (this.loadGen || 0) + 1;
      this.inflight.forEach((img) => {
        img.onload = img.onerror = null;
        img.src = '';
      });
      this.queue = [];
      this.queued = false;
      this.flush();
      this.classList.remove('zfss--live');
      this.style.removeProperty('--zfss-bg-live');
      this.scenes.forEach((s) => {
        s.el.classList.remove('is-mid', 'is-past');
        if (s.card) s.card.style.opacity = '';
        s.el.classList.toggle('is-active', s.id === 0);
        if (!s.layer) return;
        s.layer.classList.remove('is-near');
        s.layer.classList.toggle('is-active', s.id === 0);
        s.layer.style.removeProperty('--zfss-p');
      });
    }

    /* Releases every decoded frame. Compressed images stay in the browser cache. */
    flush() {
      this.cache.forEach((bmp) => bmp.close());
      this.cache.clear();
      this.pending.clear();
      this.gen = (this.gen || 0) + 1;
      this.drawn = -1;
      this.drawnScene = -9;
      this.showCanvas(false);
    }

    onFirst() {
      FIRST.forEach((t) => window.removeEventListener(t, this.onFirst));
      this.interacted = true;
      this.markNear();
      this.load();
    }

    remeasure() {
      if (!this.live) return;
      this.stale = true;
      this.schedule();
    }

    schedule() {
      if (this.live && !this.raf) this.raf = requestAnimationFrame(this.tick);
    }

    /* The only place layout sizes are read. Scene positions are relative to the pinned block. */
    measure() {
      this.stale = false;
      this.vh = window.innerHeight;
      var card = this.scenes[0].card;
      var top = card ? parseFloat(getComputedStyle(card).top) : NaN;
      this.cardTop = isNaN(top) ? this.vh * 0.58 : top;
      this.scenes.forEach((s) => {
        var cs = getComputedStyle(s.el);
        s.top = s.el.offsetTop;
        s.h = s.el.offsetHeight || 1;
        s.pad = parseFloat(cs.paddingTop) || 0;
        // scroll distance over which the next step pushes this card out of its parked position
        s.exit = (s.card ? s.card.offsetHeight : 0) + (parseFloat(cs.paddingBottom) || 0) || 1;
      });
    }

    tick() {
      this.raf = 0;
      if (!this.live || !this.near) return;
      if (this.stale) this.measure();
      var sc = this.scenes;
      // The ruler is the text: a line just under where the card parks, measured against each step.
      var line = this.cardTop + this.vh * 0.02 - this.pin.getBoundingClientRect().top;
      var idx = 0;
      for (var i = 1; i < sc.length; i++) if (line >= sc[i].top) idx = i;
      var s = sc[idx];
      var p = clamp((line - s.top - s.pad) / Math.max(1, s.h - s.pad));
      if (idx !== this.active) this.setActive(idx);

      // The card fades over the last part of its push by the next step, so it never travels
      // across the picture.
      var fade = s.exit * 0.7;
      var out = idx < sc.length - 1 ? clamp((line - (s.top + s.h - fade)) / fade) : 0;
      var o = out ? (1 - out).toFixed(2) : '';
      if (s.card && s.o !== o) {
        s.o = o;
        s.card.style.opacity = o;
      }
      var mid = p >= 0.3;
      if (s.mid !== mid) {
        s.mid = mid;
        s.el.classList.toggle('is-mid', mid);
      }
      if (s.n) {
        this.drawFrame(s, Math.round(clamp(p / HOLD) * (s.n - 1)), p);
      } else {
        this.showCanvas(false);
        var v = p.toFixed(3);
        if (s.layer && s.pv !== v) {
          s.pv = v;
          s.layer.style.setProperty('--zfss-p', v);
        }
      }
      if (this.rail) {
        var from = sc[0].top + sc[0].pad;
        var last = sc[sc.length - 1];
        var t = clamp((line - from) / Math.max(1, last.top + last.h - from)).toFixed(4);
        if (t !== this.rv) {
          this.rv = t;
          this.rail.style.transform = 'scaleY(' + t + ')';
        }
      }
    }

    setActive(idx) {
      this.active = idx;
      this.scenes.forEach((s) => {
        s.el.classList.toggle('is-active', s.id === idx);
        s.el.classList.toggle('is-past', s.id < idx); // cards of earlier scenes stay hidden
        if (s.id !== idx && s.card && s.o) {
          s.o = '';
          s.card.style.opacity = '';
        }
        if (s.layer) s.layer.classList.toggle('is-active', s.id === idx);
      });
      this.markNear();
      var bg = this.scenes[idx].bg;
      if (bg) this.style.setProperty('--zfss-bg-live', bg);
      else this.style.removeProperty('--zfss-bg-live');
      if (this.queue.length) this.sortQueue();
    }

    /* Lays out the stills of the active scene and, once the visitor has interacted, its neighbours.
       Layers that are not laid out never download their images. */
    markNear() {
      var reach = this.interacted ? 1 : 0;
      this.scenes.forEach((s) => {
        if (!s.layer) return;
        var on = Math.abs(s.id - this.active) <= reach;
        s.layer.classList.toggle('is-near', on);
        if (on && this.interacted && !s.eager) {
          s.eager = true;
          s.layer.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        }
      });
    }

    showCanvas(on) {
      if (this.canvasOn === on) return;
      this.canvasOn = on;
      this.classList.toggle('zfss--canvas', on);
    }

    frameUrl(s, i) {
      var at = s.url.lastIndexOf('-001.');
      return s.url.slice(0, at) + '-' + ('00' + (i + 1)).slice(-3) + s.url.slice(at + 4);
    }

    /* Queues frames coarse to fine (ends, every 16th, 8th, 4th, 2nd, then every frame on desktop),
       nearest scene first, so a scene can be scrubbed roughly after a handful of files. */
    load() {
      if (!this.live || this.lite || this.queued || !this.interacted || !this.near) return;
      this.queued = true;
      this.scenes.forEach((s) => {
        if (!s.n) return;
        var seen = {};
        var add = (i, pass) => {
          if (seen[i]) return;
          seen[i] = 1;
          this.queue.push({ s: s, i: i, pass: pass });
        };
        add(0, 0);
        add(s.n - 1, 0);
        for (var step = 16, pass = 1; step >= this.stride; step >>= 1, pass++) {
          for (var i = 0; i < s.n; i += step) add(i, pass);
        }
      });
      this.sortQueue();
      for (var k = 0; k < this.parallel; k++) this.next();
    }

    sortQueue() {
      var a = Math.max(0, this.active);
      this.queue.forEach((q) => (q.rank = q.pass + Math.abs(q.s.id - a) * 1.5));
      this.queue.sort((x, y) => x.rank - y.rank || x.i - y.i);
    }

    next() {
      var q = this.queue.shift();
      if (!q) return;
      var img = new Image();
      var gen = this.loadGen;
      var done = (ok) => {
        img.onload = img.onerror = null;
        this.inflight.splice(this.inflight.indexOf(img), 1);
        if (gen !== this.loadGen) return;
        if (ok) {
          this.loads++;
          q.s.imgs[q.i] = img;
          this.schedule();
        } else if (++this.fails > 8 && !this.loads) {
          this.queue = []; // frames are not there: stay on the posters
          return;
        }
        this.next();
      };
      img.decoding = 'async';
      if ('fetchPriority' in img) img.fetchPriority = 'low';
      img.onload = () => done(true);
      img.onerror = () => done(false);
      this.inflight.push(img);
      img.src = this.frameUrl(q.s, q.i);
    }

    drawFrame(s, f, p) {
      var want = s.base + f;
      var best = -1;
      this.target = want;
      for (var d = 0; d < s.n && best < 0; d++) {
        if (f - d >= 0 && this.cache.has(want - d)) best = want - d;
        else if (f + d < s.n && this.cache.has(want + d)) best = want + d;
      }
      if (best > -1) {
        if (best !== this.drawn) {
          this.ctx.drawImage(this.cache.get(best), 0, 0, this.size, this.size);
          this.drawn = best;
          this.drawnScene = s.id;
        }
        this.showCanvas(true);
      } else if (Math.abs(this.drawnScene - s.id) > 1) {
        // Nothing decoded for this scene yet. A frame from this or a neighbouring sequence may
        // stay up (the sequences connect); otherwise fall back to the poster.
        this.showCanvas(false);
      }
      // Decode ahead of the scroll position, never in the draw path.
      // Frames asked for in this tick are protected from eviction, so a small
      // cache (iOS) cannot throw away a frame it has just decoded.
      this.keep = new Set();
      var w = this.stride * 4;
      for (var i = f - w; i <= f + w; i++) this.decode(s, i);
      var nb = p > 0.7 ? this.scenes[s.id + 1] : p < 0.12 ? this.scenes[s.id - 1] : null;
      if (nb && nb.n) {
        for (var j = 0; j <= w; j++) this.decode(nb, p > 0.7 ? j : nb.n - 1 - j);
      }
    }

    decode(s, i) {
      var img = s.imgs[i];
      var key = s.base + i;
      if (this.keep) this.keep.add(key);
      if (!img || this.cache.has(key) || this.pending.has(key)) return;
      var gen = this.gen;
      this.pending.add(key);
      (img.decode ? img.decode().catch(function () {}) : Promise.resolve())
        .then(() => createImageBitmap(img))
        .then(
          (bmp) => {
            if (gen !== this.gen) return bmp.close();
            this.pending.delete(key);
            this.cache.set(key, bmp);
            if (this.cache.size > this.maxDecoded) this.evict();
            this.schedule();
          },
          () => {
            if (gen === this.gen) this.pending.delete(key);
          }
        );
    }

    /* Drops the decoded frame furthest from the one on screen. */
    evict() {
      var worst = -1;
      var far = -1;
      this.cache.forEach((bmp, key) => {
        if (this.keep && this.keep.has(key)) return;
        var d = Math.abs(key - this.target);
        if (d > far) {
          far = d;
          worst = key;
        }
      });
      if (worst < 0) return;
      this.cache.get(worst).close();
      this.cache.delete(worst);
    }

    /* Theme editor: bring the selected scene (or open the selected accordion) into view. */
    onSelect(event) {
      var el = event.target;
      if (el.tagName === 'DETAILS') el.open = true;
      var s = this.live ? this.scenes.filter((x) => x.el === el)[0] : null;
      if (!s) {
        if (el.scrollIntoView) el.scrollIntoView({ block: 'center' });
        return;
      }
      this.measure();
      var top = this.pin.getBoundingClientRect().top + window.scrollY;
      window.scrollTo(0, top + s.top + s.pad + (s.h - s.pad) * 0.5 - this.cardTop);
    }
  }

  customElements.define('zf-scroll-story', ZfScrollStory);
})();
